import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  Account,
  CustomerBonus,
  FastSumsTypes,
  IntervalsTypes,
  PaymentMethodConfiguration,
  PaymentTypes,
  RedirectContainers,
  TaxConfiguration,
} from '@cashier/cashier-model';

export interface PaymentMethodState {
  paymentMethod: PaymentMethodConfiguration;
  bonus: CustomerBonus;
  accounts: Account[];
  country: string | null;
  paymentIqMerchantId: string | null;
  tax: {
    [key: string]: TaxConfiguration;
  };
  error: string | null;
  registrationRequired: boolean;
  areTranslationsReady: boolean;
  isPaymentMethodReady: boolean;
}

export const PAYMENT_METHOD_FEATURE_KEY = 'paymentMethod';

export const initialPaymentMethodState: PaymentMethodState = {
  paymentMethod: {
    id: '',
    name: '',
    vendor: '',
    inputs: [],
    activeInputs: [],
    account_form_inputs: [],
    categories: [],
    logo: '',
    ruleParams: {
      min: null,
      max: null,
      recommended: false,
      amounts: [],
      step: null,
    },
    next_step_info: {
      channel: null,
      post_confirm: null,
      post_process: null,
    },
    description: '',
    source: null,
    referenceId: '',
    redirectContainer: RedirectContainers.NO_REDIRECT,
    preprocessor: null,
    restriction: null,
  },
  bonus: {
    amountIntervals: [],
    cashierConfiguration: {
      displayBonusFastSums: false,
      displayBonusCalculator: false,
      iconURL: '',
    },
    id: '',
    fastSumsType: FastSumsTypes.Amount,
    intervalsType: IntervalsTypes.Reward,
  },
  accounts: [],
  country: null,
  paymentIqMerchantId: null,
  tax: {},
  error: null,
  registrationRequired: false,
  areTranslationsReady: false,
  isPaymentMethodReady: false,
};

export const paymentMethodSlice = createSlice({
  name: PAYMENT_METHOD_FEATURE_KEY,
  initialState: initialPaymentMethodState,
  reducers: {
    fetchCertainPaymentMethodStart: (
      state,
      _: PayloadAction<{
        paymentType: PaymentTypes;
        paymentMethodId: string;
        bonusCode?: string;
      }>,
    ) => {
      state.isPaymentMethodReady = false;
      state.paymentMethod = { ...state.paymentMethod, inputs: [] };
    },
    fetchCertainPaymentMethodSuccess: (
      state,
      { payload }: PayloadAction<any>,
    ) => {
      const { method } = payload;
      state.paymentMethod = {
        ...state.paymentMethod,
        ...method,
      };

      state.accounts = payload.accounts || [];
      state.bonus = payload.attributes.bonus;
      state.paymentIqMerchantId = payload.attributes?.payment_iq_merchant_id;
      state.tax = payload.attributes?.tax || {};
      state.country = payload.country;
      state.registrationRequired = payload.registrationRequired;
    },
    fetchCertainPaymentMethodFailure: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.error = action.payload;
    },
    refetchCertainPaymentMethodStart: (
      _,
      __: PayloadAction<{
        paymentType: PaymentTypes;
        paymentMethodId: string;
        bonusCode?: string;
      }>,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
    ) => {},
    refetchCertainPaymentMethodSuccess: (
      state,
      { payload }: PayloadAction<any>,
    ) => {
      const { method } = payload;
      state.paymentMethod = {
        ...state.paymentMethod,
        referenceId: method.referenceId,
      };
    },
    refetchCertainPaymentMethodFailure: (
      state,
      action: PayloadAction<string>,
    ) => {
      state.error = action.payload;
    },
    /*
     * Carries no state of its own since the v1 payment-method list was removed.
     * Kept as a signal: commonMiddleware reacts to it by returning to the payment form.
     */
    resetPaymentMethod: () => undefined,
    onTranslationsReady: (state) => {
      state.areTranslationsReady = true;
    },
    onPaymentMethodReady: (state) => {
      state.isPaymentMethodReady = true;
    },
  },
});

export const paymentMethodReducer = paymentMethodSlice.reducer;

export const {
  fetchCertainPaymentMethodStart,
  fetchCertainPaymentMethodSuccess,
  fetchCertainPaymentMethodFailure,
  refetchCertainPaymentMethodStart,
  refetchCertainPaymentMethodSuccess,
  refetchCertainPaymentMethodFailure,
  resetPaymentMethod,
  onPaymentMethodReady,
  onTranslationsReady,
} = paymentMethodSlice.actions;
